import { splitOffset } from '../gen/context.js';
import type { Expr } from '../ir.js';

/** Two expressions written the same way, whatever their ids. */
export function sameExpr(a: Expr, b: Expr): boolean {
  const strip = (e: Expr): unknown =>
    JSON.parse(JSON.stringify(e, (key, value: unknown) => (key === 'id' ? undefined : value)));
  return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
}

/**
 * How long the piece `s[from:end]` / `s.slice(from, end)` is: the blocks say how long a piece
 * of text is, Python and JavaScript say where it stops. Written the way the views write it
 * (`s[i:i + 3]`, `s[:k]`, `s[2:5]`), the length comes back exactly as the block had it.
 */
export function sliceLength(from: Expr, end: Expr, id: () => string): Expr {
  if (from.k === 'num' && from.value === 0) return end;
  if (from.k === 'num' && end.k === 'num')
    return { k: 'num', id: id(), value: end.value - from.value };
  if (end.k === 'arith' && end.op === '+' && sameExpr(end.left, from)) return end.right;
  // `s[n - 2:n]`: both ends counted from the same place, so the length is a plain number.
  const [fromBase, fromAt] = splitOffset(from);
  const [endBase, endAt] = splitOffset(end);
  if (sameExpr(fromBase, endBase)) return { k: 'num', id: id(), value: endAt - fromAt };
  return { k: 'arith', id: id(), op: '-', left: end, right: from };
}
